import type { ProductKey, UiProductType } from '../types';

/**
 * Self-contained product configuration mapping for AtlasKit logo components
 * Maps UiProductType values to their corresponding AtlasKit logo component names
 */

export interface ProductLogoConfig {
  /** Logo component name from @atlaskit/logo */
  logo?: string;
  /** Icon component name from @atlaskit/logo */
  icon: string;
  /** Display name for the product */
  displayName: string;
  /** Corresponding product key */
  productKey: ProductKey;
}

/**
 * Product type to AtlasKit logo component mapping
 * Keys are lowercase UiProductType values
 * Based on patterns from product-config in Admin hub
 */
export const PRODUCT_LOGO_MAP: Record<
  Lowercase<UiProductType>,
  ProductLogoConfig
> = {
  // All UiProductType values mapped to logo configurations
  access: {
    icon: 'AdminIcon',
    displayName: 'Atlassian Administration',
    productKey: 'access',
  },
  analytics: {
    icon: 'AtlassianAnalyticsIcon',
    displayName: 'Analytics',
    productKey: 'avp',
  },
  cmdb: {
    icon: 'AssetsIcon',
    displayName: 'Assets',
    productKey: 'cmdb',
  },
  atlassian_together: {
    icon: 'AtlassianIcon',
    displayName: 'Atlassian Together',
    productKey: 'atlassian-together',
  },
  avocado: {
    icon: 'AtlassianIcon',
    displayName: 'Avocado',
    productKey: 'avocado',
  },
  beacon: {
    icon: 'GuardIcon',
    displayName: 'Guard Detect',
    productKey: 'beacon',
  },
  bitbucket: {
    logo: 'BitbucketLogo',
    icon: 'BitbucketIcon',
    displayName: 'Bitbucket',
    productKey: 'bitbucket',
  },
  cloud_admin: {
    logo: 'AtlassianAdministrationLogo',
    icon: 'AtlassianAdministrationIcon',
    displayName: 'Atlassian Administration',
    productKey: 'cloud-admin',
  },
  compass: {
    logo: 'CompassLogo',
    icon: 'CompassIcon',
    displayName: 'Compass',
    productKey: 'compass',
  },
  confluence: {
    logo: 'ConfluenceLogo',
    icon: 'ConfluenceIcon',
    displayName: 'Confluence',
    productKey: 'confluence',
  },
  devai: {
    icon: 'RovoDevIcon',
    displayName: 'Rovo Dev',
    productKey: 'devai',
  },
  fabric: {
    icon: 'AtlassianIcon',
    displayName: 'Fabric',
    productKey: 'canvas',
  },
  feedback: {
    icon: 'AtlassianIcon',
    displayName: 'Feedback',
    productKey: 'feedback',
  },
  goal: {
    icon: 'GoalsIcon',
    displayName: 'Goals',
    productKey: 'goal',
  },
  jira_admin: {
    logo: 'JiraLogo',
    icon: 'JiraIcon',
    displayName: 'Jira Administration',
    productKey: 'jira-admin',
  },
  jira_align: {
    logo: 'JiraAlignLogo',
    icon: 'JiraAlignIcon',
    displayName: 'Jira Align',
    productKey: 'jira-align',
  },
  jira_core: {
    logo: 'JiraLogo',
    icon: 'JiraIcon',
    displayName: 'Jira',
    productKey: 'jira-core',
  },
  jira_customer_service: {
    logo: 'JiraServiceManagementLogo',
    icon: 'JiraServiceManagementIcon',
    displayName: 'Jira Customer Service',
    productKey: 'jira-customer-service',
  },
  jira_family: {
    logo: 'JiraLogo',
    icon: 'JiraIcon',
    displayName: 'Jira',
    productKey: 'jira-family',
  },
  jira_incident_manager: {
    logo: 'JiraServiceManagementLogo',
    icon: 'JiraServiceManagementIcon',
    displayName: 'Jira Incident Manager',
    productKey: 'jira-incident-manager',
  },
  jira_product_discovery: {
    logo: 'JiraProductDiscoveryLogo',
    icon: 'JiraProductDiscoveryIcon',
    displayName: 'Jira Product Discovery',
    productKey: 'jira-product-discovery',
  },
  jira_service_desk: {
    logo: 'JiraServiceManagementLogo',
    icon: 'JiraServiceManagementIcon',
    displayName: 'Jira Service Desk',
    productKey: 'jira-servicedesk',
  },
  jira_service_management: {
    logo: 'JiraServiceManagementLogo',
    icon: 'JiraServiceManagementIcon',
    displayName: 'Jira Service Management',
    productKey: 'jira-servicedesk',
  },
  jira_software: {
    logo: 'JiraLogo',
    icon: 'JiraIcon',
    displayName: 'Jira',
    productKey: 'jira-software',
  },
  jira_work_management: {
    logo: 'JiraWorkManagementLogo',
    icon: 'JiraWorkManagementIcon',
    displayName: 'Jira Work Management',
    productKey: 'jira-work-management',
  },
  loom: {
    logo: 'LoomLogo',
    icon: 'LoomIcon',
    displayName: 'Loom',
    productKey: 'loom',
  },
  loom_attribution: {
    logo: 'LoomLogo',
    icon: 'LoomIcon',
    displayName: 'Loom Attribution',
    productKey: 'loom-attribution',
  },
  mercury: {
    icon: 'FocusIcon',
    displayName: 'Focus',
    productKey: 'mercury',
  },
  opsgenie: {
    logo: 'OpsgenieLogo',
    icon: 'OpsgenieIcon',
    displayName: 'Opsgenie',
    productKey: 'opsgenie',
  },
  passionfruit: {
    icon: 'AtlassianIcon',
    displayName: 'Passionfruit',
    productKey: 'passionfruit',
  },
  project: {
    icon: 'ProjectsIcon',
    displayName: 'Project',
    productKey: 'project',
  },
  radar: {
    icon: 'TalentIcon',
    displayName: 'Talent',
    productKey: 'radar',
  },
  rovo: {
    logo: 'RovoLogo',
    icon: 'RovoIcon',
    displayName: 'Rovo',
    productKey: 'rovo',
  },
  statuspage: {
    logo: 'StatuspageLogo',
    icon: 'StatuspageIcon',
    displayName: 'Statuspage',
    productKey: 'statuspage',
  },
  stride: {
    icon: 'AtlassianIcon',
    displayName: 'Stride',
    productKey: 'hipchat.cloud',
  },
  studio: {
    icon: 'StudioIcon',
    displayName: 'Studio',
    productKey: 'studio',
  },
  teamcentral: {
    icon: 'AtlassianIcon',
    displayName: 'Team Central',
    productKey: 'townsquare',
  },
  teams: {
    icon: 'TeamsIcon',
    displayName: 'Teams',
    productKey: 'teams',
  },
  trello: {
    logo: 'TrelloLogo',
    icon: 'TrelloIcon',
    displayName: 'Trello',
    productKey: 'trello',
  },
  unified_help: {
    icon: 'AtlassianIcon',
    displayName: 'Unified Help',
    productKey: 'unified-help',
  },
  unknown_atlassian: {
    icon: 'AtlassianIcon',
    displayName: 'Unknown Atlassian',
    productKey: 'unknown-atlassian',
  },
};

/**
 * Get product configuration for a given product key
 * @param productKey - The product key to look up
 * @returns ProductLogoConfig if found, null otherwise
 */
export function getProductConfig(
  productType: UiProductType,
): ProductLogoConfig | null {
  const config =
    PRODUCT_LOGO_MAP[productType.toLowerCase() as Lowercase<UiProductType>];
  return config || null;
}

/**
 * Get all available product configurations
 * @returns Array of all product configurations
 */
export function getAllProductConfigs(): ProductLogoConfig[] {
  return Object.values(PRODUCT_LOGO_MAP);
}

/**
 * Get the display name for a product, falling back to the raw product name
 * if no configuration is found
 */
export const getProductDisplayName = (productType: UiProductType): string => {
  const config = getProductConfig(productType);
  return config?.displayName || productType;
};

export const getProductType = (
  productKey: ProductKey,
): UiProductType | null => {
  // Find the UiProductType key that matches the productKey
  const productTypeEntry = Object.entries(PRODUCT_LOGO_MAP).find(
    ([, value]) => value.productKey === productKey,
  );
  return productTypeEntry ? (productTypeEntry[0] as UiProductType) : null;
};

/**
 * Get product configuration by ProductKey
 * @param productKey - The product key to look up
 * @returns ProductLogoConfig if found, null otherwise
 */
export const getProductConfigByKey = (
  productKey: ProductKey,
): ProductLogoConfig | null => {
  const config = Object.values(PRODUCT_LOGO_MAP).find(
    (config) => config.productKey === productKey,
  );
  return config || null;
};
